import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

export interface GoogleDriveStatus {
  connected: boolean;
  email: string;
  display_name: string;
  root_folder_id: string;
  shared_drive_id: string;
  shared_drive_name: string;
}

export interface SharedDriveItem {
  id: string;
  name: string;
}

export const googleDriveApiClient = createApi({
  reducerPath: 'googleDriveApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['GoogleDriveStatus', 'SharedDrives'],
  endpoints: (builder) => ({
    getGoogleDriveStatus: builder.query<GoogleDriveStatus, { firmId: string }>({
      query: ({ firmId }) =>
        `api/integrations/google-drive/status?firm_id=${firmId}`,
      providesTags: (_result, _error, { firmId }) => [
        { type: 'GoogleDriveStatus', id: firmId },
      ],
    }),
    disconnectGoogleDrive: builder.mutation<{ message: string }, { firmId: string }>({
      query: ({ firmId }) => ({
        url: `api/integrations/google-drive/disconnect?firm_id=${firmId}`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { firmId }) => [
        { type: 'GoogleDriveStatus', id: firmId },
      ],
    }),
    updateGoogleDriveRootFolder: builder.mutation<
      { message: string; root_folder_id: string },
      { firmId: string; rootFolderId: string }
    >({
      query: ({ firmId, rootFolderId }) => ({
        url: `api/integrations/google-drive/root-folder?firm_id=${firmId}`,
        method: 'PATCH',
        body: { root_folder_id: rootFolderId },
      }),
      invalidatesTags: (_result, _error, { firmId }) => [
        { type: 'GoogleDriveStatus', id: firmId },
      ],
    }),
    listGoogleDriveSharedDrives: builder.query<
      { drives: SharedDriveItem[] },
      { firmId: string }
    >({
      query: ({ firmId }) =>
        `api/integrations/google-drive/shared-drives?firm_id=${firmId}`,
      providesTags: (_result, _error, { firmId }) => [
        { type: 'SharedDrives', id: firmId },
      ],
    }),
    updateGoogleDriveSharedDrive: builder.mutation<
      { message: string; shared_drive_id: string; shared_drive_name: string },
      { firmId: string; sharedDriveId: string; sharedDriveName: string }
    >({
      query: ({ firmId, sharedDriveId, sharedDriveName }) => ({
        url: `api/integrations/google-drive/shared-drive?firm_id=${firmId}`,
        method: 'PATCH',
        body: {
          shared_drive_id: sharedDriveId,
          shared_drive_name: sharedDriveName,
        },
      }),
      invalidatesTags: (_result, _error, { firmId }) => [
        { type: 'GoogleDriveStatus', id: firmId },
      ],
    }),
  }),
});

export const {
  useGetGoogleDriveStatusQuery,
  useDisconnectGoogleDriveMutation,
  useUpdateGoogleDriveRootFolderMutation,
  useListGoogleDriveSharedDrivesQuery,
  useUpdateGoogleDriveSharedDriveMutation,
} = googleDriveApiClient;
